/**
 * Fail-closed input admission for the browser-side Navier-Stokes pipe model.
 *
 * The legacy path sometimes converted invalid geometry/properties into a
 * zero-Reynolds-number or default-diameter solve. That makes a malformed model
 * look executable. These diagnostics retain the exact component and field
 * address so the editor can direct the user to the bad value.
 */

export const NS_PIPE_INPUT_INVALID = 'NS_PIPE_INPUT_INVALID' as const;

export interface NSPipeInputDiagnostic {
  readonly code: typeof NS_PIPE_INPUT_INVALID;
  readonly severity: 'error';
  readonly stage: 'admission';
  readonly componentId: string;
  readonly field: string;
  readonly value: unknown;
  readonly requirement: string;
  readonly message: string;
  readonly recoveryActions: readonly string[];
}

/** Typed, field-addressable failure raised before invalid pipe physics runs. */
export class NSPipeInputAdmissionError extends RangeError {
  readonly code = NS_PIPE_INPUT_INVALID;
  readonly componentId: string;
  readonly field: string;
  readonly diagnostic: NSPipeInputDiagnostic;

  constructor(
    componentId: string,
    field: string,
    value: unknown,
    requirement: string,
    recoveryAction: string,
  ) {
    const renderedValue = typeof value === 'number' && Number.isNaN(value)
      ? 'NaN'
      : String(value);
    const message = `${field} ${requirement} (got ${renderedValue}).`;
    super(message);
    this.name = 'NSPipeInputAdmissionError';
    this.componentId = componentId;
    this.field = field;
    this.diagnostic = Object.freeze({
      code: NS_PIPE_INPUT_INVALID,
      severity: 'error',
      stage: 'admission',
      componentId,
      field,
      value,
      requirement,
      message,
      recoveryActions: Object.freeze([recoveryAction]),
    });
  }
}

/** Admit one finite, strictly positive scalar and preserve its field address. */
export function admitFinitePositivePipeInput(
  componentId: string,
  field: string,
  value: unknown,
  quantity: string,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new NSPipeInputAdmissionError(
      componentId,
      field,
      value,
      'must be a finite number greater than zero',
      `Enter a finite positive ${quantity}.`,
    );
  }
  return value;
}

/** Admit absolute roughness and enforce the Darcy-pipe geometry domain. */
export function admitPipeRoughness(
  componentId: string,
  field: string,
  value: unknown,
  diameter: number,
  unitLabel: string = 'm',
): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new NSPipeInputAdmissionError(
      componentId,
      field,
      value,
      'must be a finite number greater than or equal to zero',
      `Enter a finite nonnegative absolute roughness in ${unitLabel}.`,
    );
  }
  if (value >= diameter) {
    throw new NSPipeInputAdmissionError(
      componentId,
      field,
      value,
      `must be less than hydraulic diameter ${diameter} ${unitLabel}`,
      'Reduce absolute roughness or increase hydraulic diameter so roughness < diameter.',
    );
  }
  return value;
}

/** Canonical-SI geometry backstop used by direct and worker pipe construction. */
export function admitCanonicalPipeGeometry(
  componentId: string,
  diameter: unknown,
  roughness: unknown,
  fieldPrefix: string = `pipes.${componentId}`,
): { diameter: number; roughness: number } {
  const admittedDiameter = admitFinitePositivePipeInput(
    componentId,
    `${fieldPrefix}.diameter`,
    diameter,
    'hydraulic diameter in m',
  );
  const admittedRoughness = admitPipeRoughness(
    componentId,
    `${fieldPrefix}.roughness`,
    roughness,
    admittedDiameter,
  );
  return { diameter: admittedDiameter, roughness: admittedRoughness };
}
